import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { applicationApi } from '@/api';
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Icon,
  LinkButton,
  ListCard,
  LoadingState,
  Pagination,
  SegmentedTabs,
  useToast,
} from '@/components/ui';
// import { RecruitmentProgress } from '@/features/applications/RecruitmentProgress';
import { APPLICATION_TONES } from '@/features/applications/status';
import { CompanyLogo } from '@/features/internships/CompanyLogo';
import { errorMessage } from '@/hooks/useApiError';
import { paths } from '@/routes/paths';
import { cn } from '@/utils/cn';
import { formatDate } from '@/utils/format';

/**
 * Every status has exactly one tab, so no application is reachable only
 * through "All".
 */
// const TABS = [
//   { key: '', label: 'All' },
//   { key: 'SUBMITTED', label: 'Submitted' },
//   { key: 'UNDER_REVIEW', label: 'In review' },
//   { key: 'SHORTLISTED', label: 'Shortlisted' },
//   { key: 'INTERVIEW', label: 'Interview' },
//   { key: 'DECISION_PENDING', label: 'Awaiting decision' },
//   {
//     key: 'OFFERED',
//     label: 'Offers',
//     statuses: ['OFFERED', 'OFFER_ACCEPTED', 'HIRED'],
//   },
//   { key: 'CLOSED', label: 'Closed', tone: 'neutral', statuses: ['REJECTED', 'WITHDRAWN'] },
// ];

// The same three stages as the company's Applications Received, so both sides
// agree on where an application stands.
const TABS = [
  { key: '', label: 'All' },
  { key: 'review', label: 'In review', statuses: ['SUBMITTED', 'UNDER_REVIEW', 'SHORTLISTED'] },
  { key: 'interview', label: 'Interview', statuses: ['INTERVIEW', 'DECISION_PENDING'] },
  {
    key: 'decided',
    label: 'Accepted / rejected',
    statuses: ['OFFERED', 'OFFER_ACCEPTED', 'HIRED', 'REJECTED', 'WITHDRAWN'],
  },
];

/** The statuses a tab asks the API for; none means every status. */
function statusesFor(key) {
  if (!key) return undefined;
  return TABS.find((tab) => tab.key === key)?.statuses ?? [key];
}

const PAGE_SIZE = 20;

// /** The road an application travels, in order. */
// const STAGES = [
//   { key: 'SUBMITTED', label: 'Applied' },
//   { key: 'UNDER_REVIEW', label: 'In review' },
//   { key: 'SHORTLISTED', label: 'Shortlisted' },
//   { key: 'INTERVIEW', label: 'Interview' },
//   { key: 'DECISION_PENDING', label: 'Decision' },
//   { key: 'OFFERED', label: 'Offer' },
//   { key: 'OFFER_ACCEPTED', label: 'Accepted' },
//   { key: 'HIRED', label: 'Joined' },
// ];

/** Statuses that end the road rather than sit on it. */
const CLOSED = new Set(['REJECTED', 'WITHDRAWN']);

// /** What happens next at each stage, in the student's words. */
// const NEXT_STEP = {
//   SUBMITTED: 'Waiting for the company to review your application.',
//   UNDER_REVIEW: 'The company is reviewing your application.',
//   SHORTLISTED: 'You are shortlisted. An interview invitation may follow.',
//   INTERVIEW: 'Interview stage. Check Interviews for the time and details.',
//   DECISION_PENDING: 'The company is making its decision.',
//   OFFERED: 'You have an offer. Reply before it expires.',
//   OFFER_ACCEPTED: 'Offer accepted. The company will share joining details.',
//   HIRED: 'You have joined. Congratulations!',
// };
//
// /**
//  * Where this application is on the road from "applied" to "joined": the
//  * current stage named in words, what happens next, and the numbered stepper
//  * the application's own page uses.
//  *
//  * Only for applications still moving -- a rejected or withdrawn one is not
//  * "at" any stage, and drawing it as stuck halfway would say something the
//  * record does not.
//  */
// function StageTracker({ status }) {
//   const current = STAGES.findIndex((stage) => stage.key === status);
//   if (current === -1) return null;
//   const stage = STAGES[current];
//   return (
//     <div
//       className="border-line bg-surface-muted/50 rounded-input border px-4 py-3"
//       aria-label={`Application progress: ${stage.label}, step ${current + 1} of ${STAGES.length}`}
//     >
//       <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
//         <p className="text-sm">
//           <span className="text-ink font-semibold">{stage.label}</span>
//           <span className="text-ink-muted">
//             {' '}
//             · Step {current + 1} of {STAGES.length}
//           </span>
//         </p>
//         <p className="text-ink-muted text-xs">{NEXT_STEP[stage.key]}</p>
//       </div>
//
//       {/* The same stepper as the application's own page, so the two read alike. */}
//       <div className="mt-3" aria-hidden="true">
//         <RecruitmentProgress status={status} labels={{ joined: 'Joined', decision: 'Decision' }} />
//       </div>
//     </div>
//   );
// }

/** The box's coloured edge, in the same meaning as its status badge. */
const EDGE_TONES = {
  success: 'border-l-success-500',
  warning: 'border-l-warning-500',
  danger: 'border-l-danger-500',
  info: 'border-l-info-500',
  neutral: 'border-l-line',
};

/** The one word a box gives its status, in the stages of the tabs above it. */
const BOX_STATUS = {
  SUBMITTED: 'In review',
  UNDER_REVIEW: 'In review',
  SHORTLISTED: 'In review',
  INTERVIEW: 'Interview',
  DECISION_PENDING: 'Interview',
  OFFERED: 'Offered',
  OFFER_ACCEPTED: 'Accepted',
  HIRED: 'Accepted',
  REJECTED: 'Rejected',
  WITHDRAWN: 'Withdrawn',
};

/** The "Applied" tab of My internships: every application and where it stands. */
export function ApplicationsPanel() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [pendingWithdrawal, setPendingWithdrawal] = useState(null);

  const { data, isPending, isPlaceholderData, isError, error, refetch } = useQuery({
    queryKey: ['student-applications', status, page],
    queryFn: () =>
      applicationApi.listApplications({
        page,
        page_size: PAGE_SIZE,
        ...(status ? { statuses: statusesFor(status) } : {}),
      }),
    // Switching tab keeps the page (and the tabs) on screen while the next
    // list loads, instead of blanking everything for a spinner.
    placeholderData: keepPreviousData,
  });

  const withdraw = useMutation({
    mutationFn: (id) => applicationApi.withdrawApplication(id),
    onSuccess: () => {
      toast.success('Application withdrawn');
      void queryClient.invalidateQueries({ queryKey: ['student-applications'] });
      void queryClient.invalidateQueries({ queryKey: ['student-dashboard'] });
      setPendingWithdrawal(null);
    },
    onError: (withdrawError) => {
      toast.error(errorMessage(withdrawError, 'Could not withdraw the application'));
      setPendingWithdrawal(null);
    },
  });

  // Under the list's key, so whatever refreshes the list refreshes these.
  const { data: counts } = useQuery({
    queryKey: ['student-applications', 'counts'],
    queryFn: applicationApi.getApplicationCounts,
  });
  const tabs = TABS.map((tab) => ({
    ...tab,
    count: counts
      ? (tab.statuses ?? Object.keys(counts)).reduce((total, key) => total + (counts[key] ?? 0), 0)
      : undefined,
  }));

  const rows = data?.data ?? [];
  const meta = data?.meta;

  return (
    <div className="flex flex-1 flex-col">
      {/* Loading and errors render inside the card, below the tabs, so
          switching tab never unmounts them. */}
      <ListCard
        fill
        className="flex-1 bg-transparent"
        isRefreshing={isPlaceholderData}
        toolbarStart={
          <SegmentedTabs
            // variant="plain"
            variant="pills"
            label="Application status"
            // tabs={TABS}
            tabs={tabs}
            value={status}
            onChange={(key) => {
              setStatus(key);
              setPage(1);
            }}
          />
        }
        state={
          isError ? (
            <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />
          ) : isPending ? (
            <LoadingState label="Loading your applications…" />
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<Icon name="application" size={22} />}
              title={status ? 'Nothing at this stage' : 'No applications yet'}
              description={
                status
                  ? 'None of your applications is at this stage right now.'
                  : 'Find an internship that fits and apply — your applications show up here.'
              }
              action={<LinkButton to={paths.internships}>Browse internships</LinkButton>}
            />
          ) : null
        }
        footer={
          meta && (
            <Pagination
              className=""
              page={meta.page}
              pageSize={PAGE_SIZE}
              totalPages={meta.total_pages}
              totalItems={meta.total_items}
              onPageChange={setPage}
            />
          )
        }
      >
        {/* The wide rows, before the client's simple boxes.
        <BoxList>
          {rows.map((row) => {
            const closed = CLOSED.has(row.status);
            const detail = paths.student.applicationDetail(row.id);
            return (
              <BoxRow
                key={row.id}
                accent={APPLICATION_TONES[row.status] ?? 'neutral'}
                leading={
                  <CompanyLogo
                    company={{
                      name: row.internship.company_name,
                      logo_url: row.internship.company_logo_url,
                    }}
                    className={cn('size-11 rounded-xl', closed && 'opacity-60 grayscale')}
                    iconSize={20}
                  />
                }
                title={row.internship.title}
                to={detail}
                badges={
                  <Badge tone={APPLICATION_TONES[row.status] ?? 'neutral'} dot>
                    {STUDENT_APPLICATION_LABELS[row.status] ?? row.status}
                  </Badge>
                }
                subtitle={[row.internship.company_name, row.internship.location]
                  .filter(Boolean)
                  .join(' · ')}
                facts={[
                  { icon: 'calendar', text: `Applied ${formatDate(row.submitted_at)}` },
                  row.reviewed_at && {
                    icon: 'eye',
                    text: `Reviewed ${formatDate(row.reviewed_at)}`,
                  },
                  // The edit itself lives on the detail page, behind "View".
                  row.can_edit && {
                    icon: 'pencil',
                    text: row.edit_deadline
                      ? `Editable until ${formatDate(row.edit_deadline)}`
                      : 'You can still edit this application',
                    tone: 'brand',
                  },
                ]}
                // When the company last moved it, so a quiet application is
                // visible as quiet.
                meta={
                  <span className="text-ink-muted text-xs">
                    Last update {formatDate(row.updated_at)}
                  </span>
                }
                // Spelled out: the main way in is the application, the listing
                // is the secondary way back.
                actions={
                  <div className="flex flex-wrap items-center justify-end gap-2">
                    <LinkButton
                      to={paths.internshipDetail(row.internship.slug)}
                      variant="secondary"
                      size="sm"
                    >
                      <Icon name="external" size={14} />
                      View listing
                    </LinkButton>
                    <LinkButton to={detail} variant="tintBrand" size="sm">
                      <Icon name="fileText" size={14} />
                      View application
                    </LinkButton>
                  </div>
                }
                // Top right, as before, but named: an unlabelled cross reads
                // as "close this card", not "withdraw my application".
                corner={
                  row.can_withdraw && (
                    <Button
                      variant="ghostDanger"
                      size="sm"
                      onClick={() => setPendingWithdrawal(row)}
                    >
                      <Icon name="close" size={14} />
                      Withdraw
                    </Button>
                  )
                }
              >
                {!closed && (
                  <div className="mt-4">
                    <StageTracker status={row.status} />
                  </div>
                )}

                {closed && (
                  <p className="text-ink-muted bg-surface-muted/70 rounded-input mt-3 flex items-start gap-2 px-3 py-2 text-sm">
                    <Icon name="info" size={16} className="mt-0.5 shrink-0" />
                    {row.status === 'REJECTED'
                      ? (row.rejection_reason ??
                        'The company decided not to go ahead with this application.')
                      : 'You withdrew this application. You can apply again later.'}
                  </p>
                )}
              </BoxRow>
            );
          })}
        </BoxList>
        */}
        {/* The first plain boxes.
        <div className="grid gap-3 p-4 md:grid-cols-2">
          {rows.map((row) => (
            <Link
              key={row.id}
              to={paths.student.applicationDetail(row.id)}
              className="border-line bg-surface rounded-card shadow-card flex items-center gap-3 border p-4 transition hover:-translate-y-0.5 hover:shadow-md"
            >
              <CompanyLogo
                company={{
                  name: row.internship.company_name,
                  logo_url: row.internship.company_logo_url,
                }}
                className={cn(
                  'size-11 rounded-xl',
                  CLOSED.has(row.status) && 'opacity-60 grayscale',
                )}
                iconSize={20}
              />
              <div className="min-w-0 flex-1">
                <p className="text-ink truncate text-sm font-semibold">{row.internship.title}</p>
                <p className="text-ink-muted truncate text-sm">{row.internship.company_name}</p>
              </div>
              <Badge tone={APPLICATION_TONES[row.status] ?? 'neutral'} dot>
                {BOX_STATUS[row.status] ?? row.status}
              </Badge>
            </Link>
          ))}
        </div>
        */}
        {/* The same box as the company dashboard's internships. */}
        <div className="grid gap-4 p-4 lg:grid-cols-2">
          {rows.map((row) => {
            const tone = APPLICATION_TONES[row.status] ?? 'neutral';
            return (
              <Card
                key={row.id}
                className={cn(
                  'flex flex-col border-l-4 transition hover:-translate-y-0.5 hover:shadow-md',
                  EDGE_TONES[tone] ?? EDGE_TONES.neutral,
                )}
              >
                <div className="flex items-start gap-3">
                  <CompanyLogo
                    company={{
                      name: row.internship.company_name,
                      logo_url: row.internship.company_logo_url,
                    }}
                    className={cn(
                      'size-11 rounded-xl',
                      CLOSED.has(row.status) && 'opacity-60 grayscale',
                    )}
                    iconSize={20}
                  />
                  <div className="min-w-0 flex-1">
                    <h3 className="text-ink truncate text-base font-semibold">
                      {row.internship.title}
                    </h3>
                    <p className="text-ink-muted mt-0.5 truncate text-sm">
                      {row.internship.company_name}
                    </p>
                  </div>
                  <Badge dot tone={tone}>
                    {BOX_STATUS[row.status] ?? row.status}
                  </Badge>
                </div>

                <div className="mt-4 flex flex-wrap gap-2 text-sm">
                  <span className="bg-surface-muted text-ink-muted inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5">
                    <Icon name="calendar" size={14} />
                    Applied {formatDate(row.submitted_at)}
                  </span>
                  {row.internship.location && (
                    <span className="bg-surface-muted text-ink-muted inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5">
                      <Icon name="home" size={14} />
                      {row.internship.location}
                    </span>
                  )}
                </div>

                <div className="border-line mt-4 flex flex-1 flex-wrap items-end justify-end gap-2 border-t pt-4">
                  {row.can_withdraw && (
                    <Button
                      variant="tintDanger"
                      size="sm"
                      onClick={() => setPendingWithdrawal(row)}
                    >
                      Withdraw
                    </Button>
                  )}
                  <LinkButton
                    to={paths.internshipDetail(row.internship.slug)}
                    variant="secondary"
                    size="sm"
                  >
                    View listing
                  </LinkButton>
                  <LinkButton to={paths.student.applicationDetail(row.id)} size="sm">
                    View Details
                  </LinkButton>
                </div>
              </Card>
            );
          })}
        </div>
      </ListCard>

      <ConfirmDialog
        open={Boolean(pendingWithdrawal)}
        title="Withdraw application"
        message={
          // Same rule as the detail page: withdrawing is always allowed, and
          // the warning tells the truth about which kind of withdrawal it is.
          `Withdraw your application for "${pendingWithdrawal?.internship.title}"? The company has not acted on it yet, and you can apply again later while the listing is open.`
        }
        confirmLabel="Withdraw"
        isLoading={withdraw.isPending}
        onConfirm={() => withdraw.mutate(pendingWithdrawal.id)}
        onCancel={() => setPendingWithdrawal(null)}
      />
    </div>
  );
}
